/**
 * Pluggable denoiser backend abstraction.
 *
 * Historically supported two backends: RNNoise (small/fast WASM) and
 * DeepFilterNet (heavier ONNX, single-thread WASM). DFN was removed in
 * 1.1.177A — see `feedback_dfn_capability_gate` + `project_voice_2026_05_26_open_work`:
 * each time we surfaced DFN automatically a new edge case appeared
 * (148A: CPU overload on weak machines; 174A: loudness drift on strong
 * ones). RNNoise alone covers the team's needs.
 *
 * The contract:
 *   1. take a raw mic `MediaStreamTrack`
 *   2. wrap it through a WASM-driven processing graph (RNNoise worklet)
 *   3. return a NEW `MediaStreamTrack` carrying cleaned audio, plus
 *      a `destroy()` callback that tears down the graph AND stops the
 *      raw track behind it so we don't leak OS-level mic captures.
 *
 * The factory is kept as a thin indirection (and DenoiserBackendName as
 * a named type) so a future denoiser swap-in / dual-backend revival
 * doesn't need a big refactor — just add a new branch here.
 */

export interface DenoiserHandle {
  /** The processed audio track to publish through LiveKit. */
  track: MediaStreamTrack
  /** Tear down the worklet / inference graph and stop the raw mic. */
  destroy: () => void
}

// DenoiserBackendName intentionally remains a named type even with a single
// member: future-proof for the next backend, keeps call-site code unchanged
// when one lands. Drop the type if no second backend is added by end of 2026.
export type DenoiserBackendName = 'rnnoise'

export interface DenoiserBackend {
  /** Stable identifier — same string the UI badge / telemetry uses. */
  readonly name: DenoiserBackendName
  /**
   * Wrap a raw mic track. Returns null if the backend failed to
   * initialise (worklet load, WASM fetch, etc.) so the caller can
   * fall back to the AGC-only graph (`agcOnly.ts`) — sendAgc still
   * levels the outgoing mic without noise suppression.
   * Implementations must NOT throw — caller relies on null/non-null.
   */
  apply(rawTrack: MediaStreamTrack): Promise<DenoiserHandle | null>
}

/**
 * Lazy-load + instantiate the requested backend.
 *
 * Throws if the dynamic import itself fails (network / build error);
 * the backend's `apply()` returning null is a recoverable runtime
 * failure (worklet didn't load on this machine) and the caller can
 * try a different backend OR the AGC-only fallback (`agcOnly.ts`).
 */
export async function loadDenoiserBackend(
  _name: DenoiserBackendName,
): Promise<DenoiserBackend> {
  const { rnnoiseBackend } = await import('./rnnoise')
  return rnnoiseBackend
}

/**
 * Mic-free pre-warm of the denoiser stack.
 *
 * Intended to run once after login (App.tsx auth effect), well before
 * the first call-join, so the heavy one-time init (RNNoise WASM +
 * worklet) is already resident/cached when the user goes live — the
 * mic comes up faster and is less likely to race a suspended audio
 * context into a silent track.
 *
 * Fire-and-forget friendly: wrapped so a warm-up failure can never
 * break the login flow, and `getUserMedia` is never called from any
 * path here.
 */
export async function prewarmDenoiser(): Promise<void> {
  if (typeof window === 'undefined') return
  try {
    const { prewarmRnnoise } = await import('./rnnoise')
    await prewarmRnnoise()
  } catch (err) {
    console.warn('[denoiser] rnnoise prewarm failed (non-fatal):', err)
  }
}
